import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart, waLink, formatAMD } from '@/context/CartContext';

export default function CartDrawer() {
  const { t } = useTranslation();
  const { lines, isOpen, closeCart, changeQty, removeItem, clear, total } = useCart();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const handleSend = () => {
    const itemLines = lines
      .map((l) => `• ${l.qty} x ${l.item.name} — ${l.item.price}`)
      .join('\n');
    const text = [
      t('order.title'),
      '',
      itemLines,
      '',
      `${t('cart.total')}: ${formatAMD(total)}`,
      '',
      `${t('cart.name')}: ${name}`,
      `${t('cart.phone')}: ${phone}`,
      `${t('cart.address')}: ${address}`,
    ].join('\n');
    window.open(waLink(text), '_blank');
    closeCart();
    clear();
    setName('');
    setPhone('');
    setAddress('');
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          onClick={closeCart}
          aria-hidden="true"
        ></div>
      )}
      <aside
        className={`fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-background-50 shadow-xl transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label={t('cart.title')}
      >
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-xl font-semibold text-foreground-950">
            <i className="ri-shopping-bag-3-line mr-2 text-primary-600"></i>
            {t('cart.title')}
          </h3>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-shopping-bag-3-line text-3xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">{t('cart.empty')}</p>
              <a
                href="#menu"
                onClick={closeCart}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                {t('cart.browse')}
              </a>
            </div>
          ) : (
            <>
              <ul className="flex flex-col gap-3">
                {lines.map((l) => (
                  <li
                    key={l.item.id}
                    className="flex items-center gap-3 rounded-lg bg-background-100 p-3"
                  >
                    <img
                      src={l.item.image}
                      alt={l.item.name}
                      className="h-16 w-16 flex-shrink-0 rounded-md object-cover object-top"
                    />
                    <div className="flex flex-1 flex-col">
                      <span className="text-sm font-semibold text-foreground-950">{l.item.name}</span>
                      <span className="text-xs text-foreground-600">{l.item.price}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => changeQty(l.item.id, -1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-background-200 text-foreground-800 hover:bg-background-300"
                        aria-label="Decrease"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-5 text-center text-sm font-semibold">{l.qty}</span>
                      <button
                        type="button"
                        onClick={() => changeQty(l.item.id, 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600"
                        aria-label="Increase"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(l.item.id)}
                      className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 hover:text-primary-600"
                      aria-label="Remove"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex items-center justify-between rounded-lg bg-secondary-100 px-4 py-3">
                <span className="text-sm font-semibold text-secondary-900">{t('cart.total')}</span>
                <span className="font-heading text-lg font-semibold text-secondary-900">
                  {formatAMD(total)}
                </span>
              </div>

              <div className="mt-4 flex flex-col gap-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('cart.name')}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('cart.phone')}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t('cart.address')}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  {t('cart.send')}
                </button>
                <button
                  type="button"
                  onClick={clear}
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-primary-600"
                >
                  <i className="ri-delete-bin-line"></i>
                  {t('cart.clear')}
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}